// apps/office/src/components/office/EmailSendDialog.jsx
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Mail, Loader2, Send } from 'lucide-react';
import { sendDocumentEmail } from '../../lib/emailService';
import { generatePdfBlob } from '../../lib/pdfUtils';

const EmailSendDialog = ({
  open, onOpenChange,
  documentType = 'quotation', // 'quotation' | 'invoice'
  document: doc,
  pdfElement, // React element used to generate the attached PDF
}) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !doc) return;
    const num = documentType === 'quotation' ? doc.quoteNumber : doc.invoiceNumber;
    setTo(doc.clientEmail || '');
    setCc('');
    setSubject(
      documentType === 'quotation'
        ? `Quotation ${num} from HD99`
        : `Invoice ${num} from HD99`
    );
    setMessage(
      `Dear ${doc.clientName || 'Customer'},\n\n` +
      `Please find attached the ${documentType} (${num}) for your project.\n\n` +
      `If you have any questions, feel free to reply to this email.\n\n` +
      `Best regards,\nHD99 Team`
    );
  }, [open, doc, documentType]);

  const handleSend = async () => {
    if (!to) { toast.error('Recipient email is required'); return; }
    if (!doc) return;

    setSending(true);
    try {
      let pdfBlob = null;
      if (pdfElement) {
        try {
          pdfBlob = await generatePdfBlob(pdfElement);
        } catch (e) {
          console.warn('PDF generation failed, sending without attachment', e);
        }
      }

      const num = documentType === 'quotation' ? doc.quoteNumber : doc.invoiceNumber;
      const result = await sendDocumentEmail({
        to, cc, subject, message,
        documentType, documentNumber: num,
        clientName: doc.clientName,
        pdfBlob,
      });

      if (result.method === 'mailto') {
        toast.info('Email client opened. Please attach the PDF manually.');
      } else {
        toast.success('Email sent successfully!');
      }
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to send: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-600" />
            Send {documentType === 'quotation' ? 'Quotation' : 'Invoice'} via Email
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>To *</Label>
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="client@example.com"
            />
          </div>

          <div>
            <Label>CC (optional)</Label>
            <Input
              type="email"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="manager@hd99.com"
            />
          </div>

          <div>
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>

          <div>
            <Label>Message</Label>
            <Textarea
              rows={7}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <p className="text-xs text-slate-500">
            ?? The PDF will be auto-generated and attached. If the backend isn't
            connected yet, the default email client will open with the message
            pre-filled.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending} className="gap-2">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailSendDialog;